import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Button,
	Form,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import {
	previewSchemaOptimizerCsv,
	previewSchemaOptimizerSitemap,
	submitSchemaOptimizerJob,
	submitSchemaOptimizerRun,
} from "../../../services/schemaOptimizerApi";
import SchemaOptimizerIconButton from "./SchemaOptimizerIconButton";
import SchemaOptimizerRunConfirmModal from "./SchemaOptimizerRunConfirmModal";
import {
	schemaOptimizerHostname,
	urlMatchesStoreDomain,
} from "./schemaOptimizerUtils";

const MAX_URLS = 50;
const MIN_CONTENT = 20;

const SOURCE_TABS = [
	{ id: "single", label: "Page URL" },
	{ id: "content", label: "Content" },
	{ id: "sitemap", label: "Sitemap" },
	{ id: "csv", label: "CSV" },
];

/** Batch API is automatic: off for page URL + content, on for sitemap + CSV. */
function useBatchApiForTab(tab) {
	return tab === "sitemap" || tab === "csv";
}

function isHttpUrl(value) {
	try {
		const parsed = new URL(value);
		return (parsed.protocol === "http:" || parsed.protocol === "https:") && parsed.hostname;
	} catch {
		return false;
	}
}

function withHttpScheme(value) {
	const trimmed = String(value || "").trim();
	if (!trimmed) return "";
	if (/^https?:\/\//i.test(trimmed)) return trimmed;
	return `https://${trimmed}`;
}

function splitOnListSeparator(text) {
	const normalized = text.replace(/\r/g, "\n");
	if (!/[,\n]/.test(normalized)) return { commit: [], remainder: normalized };
	const endsWithSeparator = /[,\n]\s*$/.test(normalized);
	const pieces = normalized.split(/[,\n]/).map((part) => part.trim());
	if (endsWithSeparator) return { commit: pieces.filter(Boolean), remainder: "" };
	const remainder = pieces.pop() ?? "";
	const commit = pieces.filter(Boolean);
	if (remainder && isHttpUrl(remainder)) {
		return { commit: [...commit, remainder], remainder: "" };
	}
	return { commit, remainder };
}

function UrlPreview({ note, urls, skipped, onRemove, disabled }) {
	if (!note && urls.length === 0 && skipped.length === 0) return null;
	return (
		<div className="mt-2">
			{note && <p className="text-muted small mb-2">{note}</p>}
			{urls.length > 0 && (
				<ul className="schema-optimizer__url-preview list-unstyled mb-0">
					{urls.map((url) => (
						<li key={url} className="d-flex align-items-center gap-2">
							<span className="small text-truncate flex-grow-1" title={url}>
								{url}
							</span>
							<SchemaOptimizerIconButton
								title="Remove URL"
								iconClass="fas fa-times"
								disabled={disabled}
								onClick={() => onRemove(url)}
							/>
						</li>
					))}
				</ul>
			)}
			{skipped.length > 0 && (
				<p className="text-warning small mb-0 mt-2">{skipped.slice(0, 5).join(" · ")}</p>
			)}
		</div>
	);
}

function submitButtonLabel(tab) {
	return tab === "content" ? "Generate schema" : "Validate & optimize schema";
}

const CSV_SAMPLE_FILENAME = "schema-optimizer-urls-sample.csv";
const CSV_SAMPLE_CONTENT = `url
https://www.example.com/products/widget
https://www.example.com/about
`;

function urlsForSingleTab(singleUrl, previewUrls) {
	const urls = [...previewUrls];
	const typed = singleUrl.trim();
	if (typed && !urls.includes(typed)) urls.push(typed);
	return urls;
}

function downloadSchemaOptimizerCsvSample() {
	const blob = new Blob([CSV_SAMPLE_CONTENT], { type: "text/csv;charset=utf-8" });
	const objectUrl = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = objectUrl;
	anchor.download = CSV_SAMPLE_FILENAME;
	anchor.click();
	URL.revokeObjectURL(objectUrl);
}

export default function SchemaOptimizerGenerateModal({
	isOpen,
	onClose,
	websiteId,
	websiteUrl,
	onSubmitted,
}) {
	const navigate = useNavigate();
	const [tab, setTab] = useState("single");
	const [singleUrl, setSingleUrl] = useState("");
	const [pageContent, setPageContent] = useState("");
	const [sitemapUrl, setSitemapUrl] = useState("");
	const [previewUrls, setPreviewUrls] = useState([]);
	const [loadedNote, setLoadedNote] = useState(null);
	const [skipped, setSkipped] = useState([]);
	const [pageUrlOverflow, setPageUrlOverflow] = useState(0);
	const [csvName, setCsvName] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [previewTruncated, setPreviewTruncated] = useState(false);
	const [previewTotalFound, setPreviewTotalFound] = useState(0);
	const [busy, setBusy] = useState(false);
	const csvFileRef = useRef(null);

	const defaultDomainUrl = websiteUrl ? withHttpScheme(websiteUrl) : "";
	const useBatchApi = useBatchApiForTab(tab);
	const storeDomainReady = isHttpUrl(defaultDomainUrl);
	const storeHostLabel = storeDomainReady ? schemaOptimizerHostname(defaultDomainUrl) : "";

	const domainValidation = useMemo(() => {
		if (tab === "content") {
			if (!storeDomainReady) {
				return {
					ok: false,
					message: "Select a store with a valid website URL before generating schema.",
					mismatches: [],
				};
			}
			return { ok: true, message: null, mismatches: [] };
		}

		if (!storeDomainReady) {
			return {
				ok: false,
				message: "Select a store with a valid website URL before optimizing schema.",
				mismatches: [],
			};
		}

		let toCheck = [];
		if (tab === "single") {
			toCheck = urlsForSingleTab(singleUrl, previewUrls);
		} else if (tab === "sitemap") {
			toCheck = [...previewUrls];
			const raw = sitemapUrl.trim();
			if (raw) {
				const normalized = withHttpScheme(raw);
				if (isHttpUrl(normalized)) toCheck.push(normalized);
			}
		} else if (tab === "csv") {
			toCheck = [...previewUrls];
		}

		const mismatches = toCheck.filter(
			(url) => isHttpUrl(url) && !urlMatchesStoreDomain(url, defaultDomainUrl)
		);
		if (mismatches.length === 0) {
			return { ok: true, message: null, mismatches: [] };
		}

		const preview = mismatches.slice(0, 3).join(", ");
		const more =
			mismatches.length > 3 ? ` (+${mismatches.length - 3} more)` : "";
		return {
			ok: false,
			message: `URLs must belong to ${storeHostLabel} (your store website). Mismatch: ${preview}${more}`,
			mismatches,
		};
	}, [
		tab,
		singleUrl,
		previewUrls,
		sitemapUrl,
		defaultDomainUrl,
		storeDomainReady,
		storeHostLabel,
	]);

	const submitBlockedByDomain = !domainValidation.ok;

	const pageUrlFieldDomainMismatch =
		tab === "single" &&
		storeDomainReady &&
		singleUrl.trim() &&
		isHttpUrl(singleUrl.trim()) &&
		!urlMatchesStoreDomain(singleUrl.trim(), defaultDomainUrl);

	const sitemapUrlDomainMismatch =
		tab === "sitemap" &&
		storeDomainReady &&
		sitemapUrl.trim() &&
		isHttpUrl(withHttpScheme(sitemapUrl.trim())) &&
		!urlMatchesStoreDomain(withHttpScheme(sitemapUrl.trim()), defaultDomainUrl);

	const resetPreview = () => {
		setPreviewUrls([]);
		setLoadedNote(null);
		setSkipped([]);
		setPageUrlOverflow(0);
		setCsvName(null);
		setPreviewTruncated(false);
		setPreviewTotalFound(0);
	};

	const resetForm = () => {
		setTab("single");
		setSingleUrl("");
		setPageContent("");
		setSitemapUrl("");
		setConfirmOpen(false);
		resetPreview();
	};

	useEffect(() => {
		if (isOpen) resetForm();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, websiteUrl]);

	const applyPreview = (preview) => {
		setPreviewUrls(preview.urls || []);
		setSkipped(preview.skipped || []);
		setPreviewTruncated(Boolean(preview.truncated));
		setPreviewTotalFound(Number(preview.totalFound ?? preview.total_found ?? 0));
		const totalFound = Number(preview.totalFound ?? preview.total_found ?? 0);
		const ready = `${preview.urls.length} page${preview.urls.length === 1 ? "" : "s"} ready`;
		const more = preview.truncated ? ` Found ${totalFound}; first ${MAX_URLS} will run.` : "";
		const skipNote = preview.skipped?.length
			? ` ${preview.skipped.length} row(s) skipped.`
			: "";
		setLoadedNote(`${ready}.${more}${skipNote}`);
		setConfirmOpen(false);
	};

	const switchTab = (next) => {
		setTab(next);
		setConfirmOpen(false);
		resetPreview();
	};

	const handleClose = () => {
		if (busy) return;
		if (confirmOpen) {
			setConfirmOpen(false);
			return;
		}
		onClose();
	};

	const showBulkConfirm = confirmOpen && (tab === "sitemap" || tab === "csv");

	const navigateToRun = (runRes, source) => {
		onSubmitted?.();
		onClose();
		setConfirmOpen(false);
		navigate(
			`/aivisibility/schema-optimizer/run?runId=${encodeURIComponent(runRes.run.runId)}`,
			{
				state: {
					schemaOptimizerRunId: runRes.run.runId,
					schemaOptimizerJobIds: runRes.run.jobIds,
					schemaOptimizerRunSource: source,
					schemaOptimizerRunUseBatchApi: useBatchApi,
				},
			}
		);
	};

	async function submitBulkRun() {
		setBusy(true);
		const runRes = await submitSchemaOptimizerRun(websiteId, {
			source: tab,
			urls: previewUrls,
			useBatchApi,
		});
		setBusy(false);
		if (!runRes.ok) {
			toast.error(runRes.message || "Could not start run");
			setConfirmOpen(false);
			return;
		}
		navigateToRun(runRes, tab);
	}

	const addPageUrls = (tokens) => {
		const seen = new Set(previewUrls);
		const next = [...previewUrls];
		const invalid = [];
		let overflow = pageUrlOverflow;
		tokens.forEach((token) => {
			if (seen.has(token)) return;
			if (!isHttpUrl(token)) {
				invalid.push(token);
				return;
			}
			if (next.length >= MAX_URLS) {
				overflow += 1;
				return;
			}
			seen.add(token);
			next.push(token);
		});
		setPreviewUrls(next);
		if (invalid.length) setSkipped((prev) => [...prev, ...invalid]);
		setPageUrlOverflow(overflow);
	};

	const handlePageUrlChange = (value) => {
		if (!/[,\r\n]/.test(value)) {
			setSingleUrl(value);
			return;
		}
		const { commit, remainder } = splitOnListSeparator(value);
		if (commit.length) addPageUrls(commit);
		setSingleUrl(remainder);
	};

	async function handleSitemapLoad() {
		if (!sitemapUrl.trim()) return;
		setBusy(true);
		const res = await previewSchemaOptimizerSitemap(websiteId, sitemapUrl.trim());
		setBusy(false);
		if (!res.ok || !res.preview) {
			toast.error(res.message || "Could not load sitemap");
			return;
		}
		applyPreview(res.preview);
	}

	async function handleCsvFile(file) {
		if (!file) return;
		setCsvName(file.name);
		const text = await file.text();
		setBusy(true);
		const res = await previewSchemaOptimizerCsv(websiteId, text);
		setBusy(false);
		if (!res.ok || !res.preview) {
			toast.error(res.message || "Could not parse CSV");
			return;
		}
		applyPreview(res.preview);
	}

	async function handleSubmit(e) {
		e.preventDefault();
		if (busy) return;
		if (submitBlockedByDomain) {
			toast.error(
				domainValidation.message || "URLs must match your store website domain."
			);
			return;
		}

		if (tab === "single") {
			const typed = singleUrl.trim();
			if (typed && !isHttpUrl(typed)) {
				toast.error("Enter a valid URL");
				return;
			}
			const urls =
				typed && !previewUrls.includes(typed) ? [...previewUrls, typed] : [...previewUrls];
			if (urls.length === 0) {
				toast.error("Enter at least one URL");
				return;
			}
			if (urls.length > MAX_URLS) {
				toast.error(`Enter at most ${MAX_URLS} URLs`);
				return;
			}
			setBusy(true);
			if (urls.length === 1) {
				const res = await submitSchemaOptimizerJob(websiteId, {
					url: urls[0],
					useBatchApi,
				});
				setBusy(false);
				if (!res.ok) {
					toast.error(res.message || "Could not start job");
					return;
				}
				onSubmitted?.();
				onClose();
				navigate(
					`/aivisibility/schema-optimizer/view?jobId=${encodeURIComponent(res.job.jobId)}`
				);
				return;
			}
			const runRes = await submitSchemaOptimizerRun(websiteId, {
				source: "manual",
				urls,
				useBatchApi,
			});
			setBusy(false);
			if (!runRes.ok) {
				toast.error(runRes.message || "Could not start run");
				return;
			}
			navigateToRun(runRes, "manual");
			return;
		}

		if (tab === "content") {
			const content = pageContent.trim();
			const domain = defaultDomainUrl;
			if (!content || content.length < MIN_CONTENT) {
				toast.error("Paste at least 20 characters of page content");
				return;
			}
			if (!isHttpUrl(domain)) {
				toast.error("Select a store with a valid website URL first");
				return;
			}
			setBusy(true);
			const res = await submitSchemaOptimizerJob(websiteId, {
				content,
				domainUrl: domain,
				useBatchApi,
			});
			setBusy(false);
			if (!res.ok) {
				toast.error(res.message || "Could not start job");
				return;
			}
			onSubmitted?.();
			onClose();
			navigate(
				`/aivisibility/schema-optimizer/view?jobId=${encodeURIComponent(res.job.jobId)}`
			);
			return;
		}

		if (previewUrls.length === 0) {
			toast.error(tab === "sitemap" ? "Load a sitemap first" : "Upload a CSV first");
			return;
		}
		setConfirmOpen(true);
	}

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleClose}
			centered
			modalClassName="schema-optimizer__generate-modal-overlay"
			className={`schema-optimizer__generate-modal${
				showBulkConfirm ? " schema-optimizer__generate-modal--confirming" : ""
			}`}
			backdrop={busy || showBulkConfirm ? "static" : true}
		>
			<ModalHeader
				tag="div"
				toggle={handleClose}
				className="schema-optimizer__generate-modal-header"
			>
				<div className="schema-optimizer__generate-modal-head">
					<div className="schema-optimizer__generate-modal-title">
						{showBulkConfirm ? "Confirm bulk run" : "Optimize JSON-LD schema"}
					</div>
					{!showBulkConfirm ? (
						<p className="schema-optimizer__generate-modal-subtitle mb-0">
							Validate, clean, and generate structured data for a page URL, pasted content,
							sitemap, or CSV.
						</p>
					) : null}
				</div>
			</ModalHeader>
			<Form onSubmit={handleSubmit}>
				<ModalBody className="schema-optimizer__generate-modal-body">
					<SchemaOptimizerRunConfirmModal
						isOpen={showBulkConfirm}
						urlCount={previewUrls.length}
						useBatchApi={useBatchApi}
						truncated={previewTruncated}
						totalFound={previewTotalFound}
						pending={busy}
						onCancel={() => {
							if (!busy) setConfirmOpen(false);
						}}
						onConfirm={() => void submitBulkRun()}
					/>
					<div
						className={`schema-optimizer__generate-modal-main${
							showBulkConfirm ? " schema-optimizer__generate-modal-main--dimmed" : ""
						}`}
						aria-hidden={showBulkConfirm ? true : undefined}
					>
					<nav
						className="schema-optimizer__source-tabs"
						role="tablist"
						aria-label="Schema input source"
					>
						{SOURCE_TABS.map((item) => (
							<button
								key={item.id}
								type="button"
								role="tab"
								aria-selected={tab === item.id}
								className={`schema-optimizer__source-tab${
									tab === item.id ? " schema-optimizer__source-tab--active" : ""
								}`}
								disabled={busy}
								onClick={() => switchTab(item.id)}
							>
								{item.label}
							</button>
						))}
					</nav>

					<div className="schema-optimizer__source-panel" role="tabpanel">
						{tab === "single" && (
							<>
								<Label for="schema-modal-url">Page URL</Label>
								<Input
									id="schema-modal-url"
									type="textarea"
									rows={2}
									value={singleUrl}
									onChange={(e) => handlePageUrlChange(e.target.value)}
									placeholder="https://example.com/page"
									disabled={busy}
									invalid={pageUrlFieldDomainMismatch}
								/>
								<p className="text-muted small mt-1 mb-0">
									Comma or newline separates URLs (max {MAX_URLS}). Multiple URLs start a batch
									run.
									{/* {storeHostLabel ? (
										<>
											{" "}
											Each URL must belong to <strong>{storeHostLabel}</strong> (your store
											website).
										</>
									) : null} */}
								</p>
								<UrlPreview
									note={
										previewUrls.length || skipped.length
											? loadedNote ||
												`${previewUrls.length} URL(s) in list${pageUrlOverflow ? ` (${pageUrlOverflow} over limit)` : ""}`
											: null
									}
									urls={previewUrls}
									skipped={skipped}
									onRemove={(url) => setPreviewUrls((prev) => prev.filter((u) => u !== url))}
									disabled={busy}
								/>
							</>
						)}
						{tab === "content" && (
							<>
								<Label for="schema-modal-domain">Domain URL</Label>
								<Input
									id="schema-modal-domain"
									value={defaultDomainUrl}
									readOnly
									tabIndex={-1}
									placeholder="Set a website URL on your store"
									disabled={busy}
									className="mb-3 schema-optimizer__domain-readonly"
								/>
								<p className="small text-muted mb-3">
									Uses your current store website URL.
								</p>
								<Label for="schema-modal-content">Page content</Label>
								<Input
									id="schema-modal-content"
									type="textarea"
									rows={5}
									value={pageContent}
									onChange={(e) => setPageContent(e.target.value)}
									disabled={busy}
								/>
							</>
						)}
						{tab === "sitemap" && (
							<>
								<Label for="schema-modal-sitemap">Sitemap URL</Label>
								<div className="schema-optimizer__sitemap-row">
									<Input
										id="schema-modal-sitemap"
										value={sitemapUrl}
										onChange={(e) => setSitemapUrl(e.target.value)}
										disabled={busy}
										invalid={sitemapUrlDomainMismatch}
									/>
									<Button
										type="button"
										color="primary"
										disabled={busy || !sitemapUrl.trim() || sitemapUrlDomainMismatch}
										onClick={handleSitemapLoad}
									>
										Load pages
									</Button>
								</div>
								<p className="text-muted small mt-1 mb-0">
									Every page in the sitemap is loaded. You confirm the count and estimated time
									before the run starts.
								</p>
								<UrlPreview
									note={loadedNote}
									urls={previewUrls}
									skipped={skipped}
									onRemove={(url) => setPreviewUrls((prev) => prev.filter((u) => u !== url))}
									disabled={busy}
								/>
							</>
						)}
						{tab === "csv" && (
							<>
								<Label for="schema-modal-csv">CSV file</Label>
								<div className="schema-optimizer__csv-upload-row">
									<Button
										type="button"
										id="schema-modal-csv"
										color="primary"
										disabled={busy}
										onClick={() => csvFileRef.current?.click()}
									>
										Upload CSV
									</Button>
									<input
										ref={csvFileRef}
										type="file"
										accept=".csv,text/csv"
										className="d-none"
										disabled={busy}
										onChange={(e) => {
											const file = e.target.files?.[0];
											if (file) void handleCsvFile(file);
											e.target.value = "";
										}}
									/>
								</div>
								<div className="schema-optimizer__csv-format-hint">
									<p className="text-muted small mb-1">
										Use a header row with a <strong>url</strong> column and one full page URL per
										row (max {MAX_URLS}). Extra columns are ignored.
									</p>
									<pre className="schema-optimizer__csv-format-example mb-2" aria-hidden>
										{CSV_SAMPLE_CONTENT.trimEnd()}
									</pre>
									<button
										type="button"
										className="btn btn-link btn-sm p-0 schema-optimizer__csv-sample-link"
										disabled={busy}
										onClick={downloadSchemaOptimizerCsvSample}
									>
										<i className="fas fa-download me-1" aria-hidden />
										Download sample CSV
									</button>
								</div>
								{storeHostLabel ? (
									<p className="text-muted small mb-0 mt-1">
										Every URL in the file must belong to <strong>{storeHostLabel}</strong>.
									</p>
								) : null}
								{csvName && <p className="text-muted small mt-1 mb-0">Loaded {csvName}</p>}
								<p className="text-muted small mb-0">
									You confirm the URL count and estimated time before the run starts.
								</p>
								<UrlPreview
									note={loadedNote}
									urls={previewUrls}
									skipped={skipped}
									onRemove={(url) => setPreviewUrls((prev) => prev.filter((u) => u !== url))}
									disabled={busy}
								/>
							</>
						)}
					</div>
					{domainValidation.message && (
						<p
							className="schema-optimizer__domain-validation-error small text-danger mb-0 mt-2"
							role="alert"
						>
							{domainValidation.message}
						</p>
					)}
					</div>
				</ModalBody>
				{!showBulkConfirm && (
					<ModalFooter className="schema-optimizer__generate-modal-footer">
						<Button color="secondary" outline type="button" onClick={handleClose} disabled={busy}>
							Cancel
						</Button>
						<Button color="primary" type="submit" disabled={busy || submitBlockedByDomain}>
							{busy ? (
								<>
									<Spinner size="sm" className="me-2" />
									Starting…
								</>
							) : (
								submitButtonLabel(tab)
							)}
						</Button>
					</ModalFooter>
				)}
			</Form>
		</Modal>
	);
}
